import { FormatService } from '@/app/common/format/format.service';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { VideoFormatEnum } from '@/app/common/general-types/briefs.dto';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { ImageService } from '@/app/services/image/image.service';
import { UltraSocialService, UltraSocialSocialAccount } from '@/app/services/ultra-social/ultra-social';
import { DialogComponent } from '@/app/atoms/dialog/dialog.component';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { forkJoin, Observable, Subscription, take } from 'rxjs';
import { MgcPortfolioInlineVideoComponent } from '../mgc-portfolio/mgc-portfolio-inline-video.component';

type UltraSnapchatPost = {
  assetImageKeys: string[] | null;
  assetImageUrls: string[] | null;
  assetMediaKey: string | null;
  assetMediaUrl: string | null;
  assetThumbnailKey: string | null;
  assetThumbnailUrl: string | null;
  caption: string | null;
  commentCount: number;
  id: string;
  likeCount: number;
  permalink: string;
  postedAt: Date;
  shareCount: number;
  thumbnailUrl: string | null;
  type: string;
  viewCount: number;
};

type UltraSnapchatAccount = {
  id: string;
  platform: 'tiktok' | 'instagram' | 'youtube' | 'snapchat';
  handle: string;
  displayName: string;
  avatarUrl: string;
  bio: string | null;
  status: string | null;
  followerCount: number;
  followingCount: number;
  likesCount: number;
  postCount: number;
};

@Component({
  selector: 'mgc-snapchat',
  imports: [
    LdgImgComponent,
    VideoThumbnailComponent,
    LoadingInlineComponent,
    MgcPortfolioInlineVideoComponent,
    DialogComponent,
  ],
  templateUrl: './mgc-snapchat.html',
  styleUrl: './mgc-snapchat.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MgcSnapchat implements OnInit, OnDestroy {
  @Input() creator!: CreatorResponseDto;
  @Input() ultra_social_snapchat: UltraSocialSocialAccount | undefined;
  @Input() ultra_social_creator_id: string | undefined;

  readonly usingUltraSocial = signal(false);
  ultra_snapchat_account = signal<UltraSnapchatAccount | undefined>(undefined);
  ultra_snapchat_posts = signal<UltraSnapchatPost[] | undefined>(undefined);

  readonly snapchatPermalink = signal<string | undefined>(undefined);
  readonly snapchatDialogOpen = computed(() => !!this.snapchatPermalink());

  private currentUserService = inject(CurrentUserService);
  subscriptionList = new Subscription();

  readonly snapchatHandleRevealed = computed(() => this.currentUserService.subscriptionActive());

  public formatService = inject(FormatService);
  public imageService = inject(ImageService);
  public ultraSocialService = inject(UltraSocialService);

  ngOnInit(): void {
    if (this.ultra_social_creator_id && this.ultra_social_snapchat) {
      this.usingUltraSocial.set(true);
      this.getUltraSnapchatAccount(this.ultra_social_creator_id, this.ultra_social_snapchat.id);
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getUltraSnapchatAccount(ultra_social_creator_id: string, socialAccountId: string) {
    this.subscriptionList.add(
      this.loadData(ultra_social_creator_id, socialAccountId).pipe(take(1)).subscribe((data) => {
        this.ultra_snapchat_account.set(data.account);
        this.ultra_snapchat_posts.set(data.posts);
      }),
    );
  }

  private loadData(ultra_social_creator_id: string, socialAccountId: string): Observable<{
    account: UltraSnapchatAccount;
    posts: UltraSnapchatPost[];
  }> {
    return forkJoin({
      account: this.ultraSocialService.getSocialAccount(ultra_social_creator_id, socialAccountId),
      posts: this.ultraSocialService.getAccountPosts(ultra_social_creator_id, socialAccountId),
    });
  }

  snapchatProfileUrl(): string | undefined {
    const handle = this.creator?.snapchatHandle?.replace('@', '').trim();
    return handle ? `https://www.snapchat.com/add/${encodeURIComponent(handle)}` : undefined;
  }

  openSnapchatPost(post: UltraSnapchatPost): void {
    const permalink = post.permalink?.trim();
    if (permalink) {
      this.snapchatPermalink.set(permalink);
    }
  }

  closeSnapchatDialog(): void {
    this.snapchatPermalink.set(undefined);
  }

  hasUltraVideoPreview(post: UltraSnapchatPost): boolean {
    return !!(
      post.assetMediaUrl ||
      post.assetThumbnailUrl ||
      post.assetThumbnailKey ||
      this.isUsableMediaUrl(post.thumbnailUrl)
    );
  }

  ultraPostImageSrc(post: UltraSnapchatPost): string | undefined {
    if (this.isUsableMediaUrl(post.assetImageUrls?.[0])) {
      return post.assetImageUrls![0];
    }
    if (post.assetImageKeys?.[0]) {
      return this.imageService.getImageSource(post.assetImageKeys[0], 'original');
    }
    return undefined;
  }

  ultraThumbnailUrl(post: UltraSnapchatPost): string | undefined {
    const url =
      this.usableUrlOrEmpty(post.assetThumbnailUrl) || this.usableUrlOrEmpty(post.thumbnailUrl);
    return url || undefined;
  }

  ultraThumbnailCdnKey(post: UltraSnapchatPost): string | undefined {
    return post.assetThumbnailKey ?? undefined;
  }

  assembledVideoObject(post: UltraSnapchatPost): VideoResponseDto {
    const thumbnailUrl =
      this.usableUrlOrEmpty(post.assetThumbnailUrl) ||
      this.usableUrlOrEmpty(post.thumbnailUrl);

    return {
      createdDate: post.postedAt,
      creator: this.creator,
      creatorId: this.creator.blazeFirebaseUid,
      fullUrl: post.assetMediaUrl ?? '',
      id: post.id,
      key: post.assetMediaKey ?? '',
      status: 1,
      thumbnail: thumbnailUrl,
      ugcBriefApplicationId: null,
      videoFormat: VideoFormatEnum.Vertical,
      videoLength: 0,
      videoName: post.caption ?? '',
      briefApplicationId: '',
      thumbnailURL: thumbnailUrl,
      thumbnailCdnKey: post.assetThumbnailKey,
    };
  }

  private isUsableMediaUrl(url: string | null | undefined): url is string {
    if (!url) {
      return false;
    }
    return !url.includes('www.tikwm.com') && !url.includes('ionicframework.com/docs/img/demos/thumbnail.svg');
  }

  private usableUrlOrEmpty(url: string | null | undefined): string {
    return this.isUsableMediaUrl(url) ? url : '';
  }
}
